import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Button,
  Text,
  Image,
  IconButton,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import arrowRight from "./Images/slider-arrow-right.svg";
import arrowLeft from "./Images/slider-arrow-left.svg";
import liveSessionsIcon from "./Images/live-sessions-icon.svg";
import feedbackReportsIcon from "./Images/feedback-reports-icon.svg";
import unlimitedSupportIcon from "./Images/unlimited-support-icon.svg";
import coursesData from "./coursesData";
import arrow from "./Images/Button-Arrow.svg";


export const CourseClassroom = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [showCourseContent, setShowCourseContent] = useState(true);

  useEffect(() => {
      // React Router already gives you a decoded param, but
      // just in case:
      const id = decodeURIComponent(courseId);
      const idx = coursesData.findIndex((c) => c.id === id);
      if (idx !== -1) {
        setSelectedIdx(idx);
      }
      // otherwise you could show a 404 or leave it alone
     }, [courseId]);

  const scrollRef = useRef(null);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const course = coursesData[selectedIdx];
  if (!course) {
    return (
      <Box p={6} textAlign="center">
        <Text fontSize="18px" mb={4}>
          Course not found.
        </Text>
        <Button onClick={() => navigate("/courses")}>Back to Courses</Button>
      </Box>
    );
  }

  const scrollPrev = () => {
    scrollRef.current?.scrollBy({ left: -160, behavior: "smooth" });
  };
  const scrollNext = () => {
    scrollRef.current?.scrollBy({ left: 160, behavior: "smooth" });
  };

  let isDragging = false;
  let startX = 0;
  let scrollLeft = 0;
  const onMouseDown = (e) => {
    isDragging = true;
    startX = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft = scrollRef.current.scrollLeft;
  };
  const onMouseLeave = () => (isDragging = false);
  const onMouseUp = () => (isDragging = false);
  const onMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <Box
      px={{ base: 4, md: 24 }}
      py={{ base: 6, md: 12 }}
      position="relative"
      pb="100px"
      mt="92px"
    >
      <VStack spacing="44px" align="stretch">
        <Text
          color="#343634"
          fontSize="20px"
          fontWeight="500"
          textAlign="left"
        >
          Select a Course
        </Text>
        <Flex align="center" gap="16px" mb="92px">
          <IconButton
            aria-label="prev"
            icon={<Image src={arrowLeft} />}
            onClick={scrollPrev}
            variant="ghost"
          />
          <HStack
            spacing="24px"
            ref={scrollRef}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
            overflowX="auto"
            maxW="780px"
            px="2px"
            className="no-scrollbar"
            css={{
              "&::-webkit-scrollbar": { display: "none" },
              scrollBehavior: "smooth",
              cursor: "grab",
            }}
          >
            {coursesData.map((c, idx) => (
              <Button
                key={c.id}
                onClick={() => {
                  setSelectedIdx(idx);
                  navigate(`/classroom/${encodeURIComponent(c.id)}`);
                  setShowCourseContent(true);
                }}
                px="16px"
                py="12px"
                h="42px"
                borderRadius="24px"
                bg={idx === selectedIdx ? "#EBF2F7" : "#0069CA"}
                color={idx === selectedIdx ? "#0069CA" : "#FFFFFF"}
                fontSize="14px"
                fontWeight="400"
                whiteSpace="nowrap"
                flexShrink={0}
                textAlign="center"
                _hover={{ bg: "#EBF2F7", color: "#0069CA" }}
              >
                {c.title}
              </Button>
            ))}
          </HStack>

          <IconButton
            aria-label="next"
            icon={<Image src={arrowRight} />}
            onClick={scrollNext}
            variant="ghost"
          />
        </Flex>

        {showCourseContent && (
          <VStack spacing="44px" align="stretch">
            <Text
              color="#343634"
              fontSize="20px"
              fontWeight="500"
              textAlign="left"
            >
              {course.title}
            </Text>
            <HStack spacing="72px" wrap="wrap">
              {[
                liveSessionsIcon,
                feedbackReportsIcon,
                unlimitedSupportIcon,
              ].map((icon, i) => (
                <Flex
                  key={i}
                  h="55px"
                  px="20px"
                  py="12px"
                  align="center"
                  gap="16px"
                  borderRadius="4px"
                  bg="#F5F5F5"
                  w={{ base: "100%", md: "380px" }}
                >
                  <Image src={icon} boxSize="24px" />
                  <Text
                    color="#343634"
                    fontSize={{ base: "12px", md: "13px" }}
                    fontWeight="400"
                    lineHeight="normal"
                  >
                    {i === 0 && "Live sessions and recorded sessions"}
                    {i === 1 &&
                      "Feedback Reports on assignments and work with our integrated AI software"}
                    {i === 2 &&
                      "Unlimited support with subject content while enrolled"}
                  </Text>
                </Flex>
              ))}
            </HStack>

            <VStack spacing="44px" align="stretch">
              <Text
                color="#343634"
                fontSize="20px"
                fontWeight="500"
                mt="24px"
              >
                Topics you will learn for {course.title.split(" ")[0]}
              </Text>
              <VStack align="stretch" spacing="16px">
                {course.topics.map((topic, i) => (
                  <Flex
                    key={i}
                    w={{ base: "100%", md: "457px" }}
                    px="10px"
                    py="8px"
                    align="center"
                    gap="10px"
                    borderRadius="8px"
                    border="1px solid #D9D9D9"
                    wrap="wrap"
                  >
                    <Text
                      color="#343634"
                      fontSize={{ base: "12px", md: "14px" }}
                      fontWeight="500"
                      w="30px"
                      textAlign="center"
                    >
                      {String(i + 1).padStart(2, "0")}
                    </Text>
                    <Text
                      color="#343634"
                      fontSize={{ base: "13px", md: "14px" }}
                      fontWeight="400"
                      textAlign="left"
                      flex="1"
                    >
                      {topic}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            </VStack>
          </VStack>
        )}
      </VStack>

      <Box
        position="fixed"
        bottom={{ base: "80px", md: "20px" }}
        right={{ base: "12px", md: "20px" }}
        zIndex="1000"
      >
        <Button
              as="a"
              href="https://stande.co/login"
              target="_blank"
              rel="noopener noreferrer"
              borderRadius="24px"
              border="2.5px solid #E8E8E8"
              px={6}
              py={3}
              variant="outline"
              boxShadow="md"
              marginBottom="78px"
              marginRight = "78px"


            >
              <Flex align="center" gap={2}>
                <Text fontSize="14px" fontWeight="400">
                    To Dashboard 
              </Text>
                <Image src={arrow} alt="arrow" boxSize="20px" />
              </Flex>
            </Button>

      </Box>
    </Box>
  );
};


